import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextButton from 'LuxKit/Button/TextButton'
import LocationFilterMenuItem from './LocationFilterMenuItem'
import TypeFilterMenuItem from './TypeFilterMenuItem'
import type {
  getTripListDateFilterOptions,
  getTripListLocationFilterOptions,
  getTripListRowCountByType,
  getTripListTypeFilterOptions,
} from './tripListFilterOptions'
import type { TripListFilters, TripListRowType } from './tripListTypes'

export type TripListFilterSection = 'date' | 'location' | 'type'

const FilterOptions = styled.div`
  padding-bottom: ${rem(4)};
`

interface Props {
  open: boolean
  /**
   * Section the modal was opened from. That section starts expanded and every
   * other one starts collapsed. When undefined (opened via "All filters") every
   * section starts expanded.
   */
  focusedSection?: TripListFilterSection
  filters: TripListFilters
  dateFilterOptions: ReturnType<typeof getTripListDateFilterOptions>
  locationFilterOptions: ReturnType<typeof getTripListLocationFilterOptions>
  typeFilterOptions: ReturnType<typeof getTripListTypeFilterOptions>
  rowCountByType: ReturnType<typeof getTripListRowCountByType>
  typeLabelByType: Record<TripListRowType, string>
  bookableRowCount: number
  showBookableOnlyOption: boolean
  hasActiveFilters: boolean
  onClose: () => void
  onDateFilterToggle: (date: string) => void
  onLocationFilterToggle: (location: string) => void
  onTypeFilterToggle: (type: TripListRowType) => void
  onBookableOnlyToggle: () => void
  onClearDateFilters: () => void
  onClearLocationFilters: () => void
  onClearTypeFilters: () => void
  onClearAllFilters: () => void
}

function TripListFiltersModal({
  open,
  focusedSection,
  filters,
  dateFilterOptions,
  locationFilterOptions,
  typeFilterOptions,
  rowCountByType,
  typeLabelByType,
  bookableRowCount,
  showBookableOnlyOption,
  hasActiveFilters,
  onClose,
  onDateFilterToggle,
  onLocationFilterToggle,
  onTypeFilterToggle,
  onBookableOnlyToggle,
  onClearDateFilters,
  onClearLocationFilters,
  onClearTypeFilters,
  onClearAllFilters,
}: Props) {
  const intl = useIntl()

  const hasDateSection = dateFilterOptions.length > 0
  const hasLocationSection = locationFilterOptions.length > 0
  const hasTypeSection = typeFilterOptions.length > 0

  // Opening from a chip focuses that one section; "All filters" expands them all.
  const isDateSectionOpen = !focusedSection || focusedSection === 'date'
  const isLocationSectionOpen = !focusedSection || focusedSection === 'location'
  const isTypeSectionOpen = !focusedSection || focusedSection === 'type'

  const resetLabel = intl.formatMessage({
    id: 'jTxteHmA2QkkNrYu0eea',
    defaultMessage: 'Reset',
    description: 'Link that clears the selections of a single filter group in the trip planner list view filters modal',
  })

  return (
    <ModalBase
      mode="drawer"
      isOpen={open}
      onClose={onClose}
    >
      <ModalHeader
        title={intl.formatMessage({
          id: '1Iq-TfV_3bf-3x5FFQ3Q',
          defaultMessage: 'Filters',
          description: 'Heading of the modal that holds every itinerary filter in the trip planner list view',
        })}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          {hasDateSection && (
            <FilterPanelFilterGroup
              data-testid="trip-list-filters-modal-date"
              title={intl.formatMessage({
                id: '-tnLU7rkM1o-VFOM-2zG',
                defaultMessage: 'Date',
                description: 'Section title for the date filter options in the trip planner list view filters modal',
              })}
              opened={isDateSectionOpen}
              canReset={filters.dates.size > 0}
              resetLabel={resetLabel}
              onReset={onClearDateFilters}
            >
              <FilterOptions>
                {dateFilterOptions.map(({ value, label, count }) => (
                  <LocationFilterMenuItem
                    key={value}
                    value={value}
                    label={label}
                    count={count}
                    selected={filters.dates.has(value)}
                    onToggle={onDateFilterToggle}
                  />
                ))}
              </FilterOptions>
            </FilterPanelFilterGroup>
          )}
          {hasLocationSection && (
            <FilterPanelFilterGroup
              data-testid="trip-list-filters-modal-location"
              title={intl.formatMessage({
                id: '6Fr_NiXloSGDFQwcq2NQ',
                defaultMessage: 'Location',
                description: 'Section title for the location filter options in the trip planner list view filters modal',
              })}
              opened={isLocationSectionOpen}
              canReset={filters.locations.size > 0}
              resetLabel={resetLabel}
              onReset={onClearLocationFilters}
            >
              <FilterOptions>
                {locationFilterOptions.map(({ location, count }) => (
                  <LocationFilterMenuItem
                    key={location.filterValue}
                    value={location.filterValue}
                    label={location.label}
                    count={count}
                    selected={filters.locations.has(location.filterValue)}
                    onToggle={onLocationFilterToggle}
                  />
                ))}
              </FilterOptions>
            </FilterPanelFilterGroup>
          )}
          {hasTypeSection && (
            <FilterPanelFilterGroup
              data-testid="trip-list-filters-modal-type"
              title={intl.formatMessage({
                id: 'Kpfyh03UdyMFh5WtK8Q1',
                defaultMessage: 'Type',
                description: 'Section title for the itinerary item type filter options in the trip planner list view filters modal',
              })}
              opened={isTypeSectionOpen}
              canReset={filters.types.size > 0 || filters.bookableOnly}
              resetLabel={resetLabel}
              onReset={onClearTypeFilters}
            >
              <FilterOptions>
                {typeFilterOptions.map(type => (
                  <TypeFilterMenuItem
                    key={type}
                    type={type}
                    label={typeLabelByType[type]}
                    count={rowCountByType[type]}
                    selected={filters.types.has(type)}
                    onToggle={onTypeFilterToggle}
                  />
                ))}
                {showBookableOnlyOption && (
                  <LocationFilterMenuItem
                    value="bookableOnly"
                    label={intl.formatMessage({
                      id: '090aRIoUKRKu6fdzG8Jo',
                      defaultMessage: 'Bookable only',
                      description: 'Filter option label for showing only bookable rows in the trip planner list view filters modal',
                    })}
                    count={bookableRowCount}
                    selected={filters.bookableOnly}
                    onToggle={onBookableOnlyToggle}
                  />
                )}
              </FilterOptions>
            </FilterPanelFilterGroup>
          )}
        </ModalContent>
      </ModalBody>
      <ModalFooter data-testid="trip-list-filters-modal-footer">
        <Group direction="horizontal" horizontalAlign="stretch" verticalAlign="center" gap={8}>
          <TextButton
            kind="tertiary"
            size="large"
            disabled={!hasActiveFilters}
            onClick={onClearAllFilters}
          >
            {intl.formatMessage({
              id: 'SXrpvxVTFZKKu9MpsmmE',
              defaultMessage: 'Reset all',
              description: 'Button that clears every itinerary filter and sort in the trip planner list view filters modal',
            })}
          </TextButton>
          <TextButton kind="primary" size="large" onClick={onClose}>
            {intl.formatMessage({
              id: 'PQcAyemVGbaW1gY0Q-Ft',
              defaultMessage: 'View items',
              description: 'Button that applies the chosen filters and closes the trip planner list view filters modal',
            })}
          </TextButton>
        </Group>
      </ModalFooter>
    </ModalBase>
  )
}

export default TripListFiltersModal
